import { api } from 'redux/api/index.api';
import Analytics from 'utils/Analytics';
import { locationType } from 'utils/constants';

export const plansApi = api.injectEndpoints({
  endpoints: builder => ({
    getPlanById: builder.query({
      query: ({ projectId, planId }) => `projects/${projectId}/plans/${planId}`,
      providesTags: (result, error, { planId }) => [{ type: 'Plan', id: planId }],
    }),
    getFolderBreadcrumbs: builder.query({
      query: ({ projectId, folderId }) => `projects/${projectId}/folders/${folderId}/breadcrumbs`,
      providesTags: (result, error, { folderId }) => [{ type: 'FolderBreadcrumbs', id: folderId }],
    }),
    fetchPlans: builder.query({
      query: ({
        projectId,
        folderId,
        limit,
        offset,
        search_term,
        groupId,
        withDisabled,
        sortPropertyName,
        sortOrder,
      }) => ({
        url: `projects/${projectId}/folder-content`,
        method: 'GET',
        params: {
          folder_id: folderId,
          limit,
          offset,
          search_name: search_term !== '' ? search_term : undefined,
          group_id: groupId,
          with_disabled: withDisabled,
          sortPropertyName,
          sortOrder,
        },
      }),
      transformResponse: (response, meta, arg) => ({
        ...response,
        contents: response.contents
          ?.filter(item => (arg.onlyFolders ? item.type === locationType.folder : true))
          .map(item => ({ ...item, __reorder__: item.name })),
      }),
      providesTags: ['Plans'],
    }),
    fetchAllPlans: builder.query({
      query: ({ projectId }) => ({
        url: `projects/${projectId}/plans`,
        method: 'GET',
      }),
      providesTags: ['Plans'],
    }),
    fetchDataItem: builder.mutation({
      query: ({ params }) => ({
        url: `projects/${params.idData}/folders/${params.itemId}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Plans', 'FolderBreadcrumbs'],
    }),
    fetchGroupContent: builder.query({
      query: ({ projectId, groupId, includeUnshared, parentId }) => ({
        url: `projects/${projectId}/folders/group-content/${groupId}`,
        method: 'GET',
        params: {
          include_unshared: includeUnshared,
          parent_id: parentId,
        },
      }),
      providesTags: ['GroupContent'],
    }),
    fetchBim: builder.query({
      query: ({ projectId, bimId }) => ({
        url: `projects/${projectId}/plans/${bimId}/bim`,
        method: 'GET',
      }),
    }),
    saveItem: builder.mutation({
      query: ({ projectId, item, itemId }) => {
        const isPlan = item.type === locationType.plan || item.type === locationType.bim;
        const query = isPlan ? `/plans/${item.id}` : `/folders/${item.id || itemId}`;

        return {
          url: `projects/${projectId}${query}`,
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'No-Store',
          },
          body: JSON.stringify(item),
        };
      },
      invalidatesTags: ['Plans'],
    }),
    deleteItem: builder.mutation({
      query: ({ projectId, item }) => {
        const isPlan = item.type === locationType.plan || item.type === locationType.bim;
        const query = isPlan ? `/plans/${item.id}` : `/folders/${item.id}`;
        return {
          url: `projects/${projectId}${query}`,
          method: 'DELETE',
        };
      },
      transformResponse: response => {
        Analytics.track('plans_plan_delete_succeed');
        return response;
      },
      invalidatesTags: (result, error, { item }) => ['Plans', { type: 'Plan', id: item.id }],
    }),
    saveOrderedItem: builder.mutation({
      query: ({ projectId, folderId, item }) => {
        const isPlan = item.type === locationType.plan || item.type === locationType.bim;
        const query = isPlan ? `/plans/${item.id}` : `/folders/${item.id}`;
        const rootMediaResourceId = folderId === undefined ? item.media_resource?.id : folderId;

        let thumbnail = null;
        if (item.thumbnail_resource_id) {
          thumbnail = {
            media_resource_id: item.thumbnail_resource_id,
            media_resource_label: item.name,
            root_media_resource_id: rootMediaResourceId,
          };
        }

        const folderParams = isPlan ? 'folder_id' : 'parent_folder_id';

        const payload = {
          ...item,
          media_resource: {
            media_resource_id: item.media_resource_id || item.media_resource?.id,
            media_resource_label: item.name,
            root_media_resource_id: rootMediaResourceId,
          },
          thumbnail,
          plan_update_model: 'null',
          [folderParams]: folderId,
          order: item.order,
        };

        return {
          url: `projects/${projectId}${query}`,
          method: 'PUT',
          headers: {
            'Cache-Control': 'No-Store',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        };
      },
      transformResponse: (response, meta, arg) => {
        if (arg?.item?.type === locationType.folder) {
          Analytics.track('Plan_folder_reorder_succeed');
        } else {
          Analytics.track('Plan_plan_reorder_succeed');
        }
        return response;
      },
      invalidatesTags: ['Plans'],
    }),
    renameItem: builder.mutation({
      query: ({ projectId, folderId, item }) => {
        const rootMediaResourceId = folderId === undefined ? item.media_resource?.id : folderId;

        let thumbnail = null;
        if (item.thumbnail_resource_id) {
          thumbnail = {
            media_resource_id: item.thumbnail_resource_id,
            media_resource_label: item.name,
            root_media_resource_id: rootMediaResourceId,
          };
        }

        const payload = {
          ...item,
          media_resource: {
            media_resource_id: item.media_resource_id || item.media_resource?.id,
            media_resource_label: item.name,
            root_media_resource_id: rootMediaResourceId,
          },
          thumbnail,
          plan_update_model: 'null',
          folder_id: folderId,
          name: item.name,
        };

        const optionPlan = {
          url: `v1.3/projects/${projectId}/plans/${item.id}`,
          method: 'PUT',
          headers: {
            'Cache-Control': 'No-Store',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        };
        const options = {
          url: `v1.3/projects/${projectId}/folders/${item.id}`,
          method: 'PUT',
          headers: {
            'Cache-Control': 'No-Store',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ...item, parent_folder_id: folderId }),
        };

        return item.type === locationType.plan ? optionPlan : options;
      },
      invalidatesTags: (result, error, { item }) => [
        'Plans',
        { type: 'Plan', id: item.id },
      ],
    }),
    deleteImage: builder.mutation({
      query: ({ projectId, imageId }) => ({
        url: `projects/${projectId}/folders/${imageId}/detach-media`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          detach_media_resource_ids: [imageId],
        }),
      }),
      invalidatesTags: ['Plans'],
    }),
    fetchZoningData: builder.query({
      queryFn: async (mediaUrl, _queryApi, _extraOptions, baseQuery) => {
        const urlResult = await baseQuery(`${mediaUrl}/url?quality=Thumbnail`);
        if (urlResult.error) return { error: urlResult.error };

        if (!urlResult.data?.media_url) {
          return { error: { status: 'CUSTOM_ERROR', error: 'No media URL found' } };
        }

        try {
          const response = await fetch(urlResult.data.media_url);
          const data = await response.json();
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: error.message } };
        }
      },
    }),
    postFolder: builder.mutation({
      query: ({ projectId, values, parentFolderId }) => ({
        url: `projects/${projectId}/folders`,
        method: 'POST',
        headers: {
          'Cache-Control': 'No-Store',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: values.name,
          id: values.id,
          parent_folder_id: parentFolderId,
        }),
      }),
      transformResponse: response => {
        Analytics.track('plans_folder_creation_succeeded');
        return response;
      },
      invalidatesTags: ['Plans'],
    }),
    fetchPlansChildrenFolders: builder.query({
      query: ({ projectId, folderId }) => {
        const folderQuery = folderId ? `/${folderId}/children-folders?` : '?';
        return {
          url: `projects/${projectId}/folders${folderQuery}offset=0&limit=500`,
          method: 'GET',
          headers: { 'Cache-Control': 'No-Store' },
        };
      },
      transformResponse: (response, meta, arg) =>
        arg.folderId
          ? response.folders
          : response.folders.filter(
              element => !element.parent_folder_id || element.parent_folder_id === null,
            ),
      providesTags: ['Plans'],
    }),
    moveFoldersTo: builder.mutation({
      query: ({ projectId, parentId, itemIds }) => ({
        url: `projects/${projectId}/folders/change-parent-folder`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: {
          parent_folder_id: parentId !== 'root' ? parentId : null,
          moved_folder_ids: itemIds,
        },
      }),
      invalidatesTags: ['Plans'],
    }),
    movePlansTo: builder.mutation({
      query: ({ projectId, parentId, itemIds }) => ({
        url: `projects/${projectId}/plans/change-parent-folder`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: {
          parent_folder_id: parentId !== 'root' ? parentId : null,
          moved_plan_ids: itemIds,
        },
      }),
      invalidatesTags: ['Plans'],
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useGetPlanByIdQuery,
  useLazyGetPlanByIdQuery,
  useGetFolderBreadcrumbsQuery,
  useLazyGetFolderBreadcrumbsQuery,
  useFetchPlansQuery,
  useLazyFetchPlansQuery,
  useFetchAllPlansQuery,
  useLazyFetchAllPlansQuery,
  useFetchDataItemMutation,
  useSaveItemMutation,
  useDeleteItemMutation,
  useSaveOrderedItemMutation,
  useRenameItemMutation,
  useFetchZoningDataQuery,
  useDeleteImageMutation,
  usePostFolderMutation,
  useFetchGroupContentQuery,
  useLazyFetchGroupContentQuery,
  useLazyFetchBimQuery,
  useFetchPlansChildrenFoldersQuery,
  useLazyFetchPlansChildrenFoldersQuery,
  useMoveFoldersToMutation,
  useMovePlansToMutation,
} = plansApi;
